/**
 * CityCompare — painel dentro do mapa que compara 2 a 4 cidades lado a lado: total
 * de cada métrica e, por dimensão (origens, referers, dispositivos...), os maiores
 * valores de cada cidade. Cada cidade tem a cor da sua borda no mapa. Clicar numa
 * métrica troca o que o mapa pinta; clicar num valor o aplica como filtro.
 */

import Icon from './icons.jsx'
import { COMPARE_COLORS, MAPA_METRICS, originLabel } from '../lib/mapaFilters.js'

const SECTIONS = [
  { key: 'origem', title: 'Origens' },
  { key: 'referer', title: 'Referers' },
  { key: 'dispositivo', title: 'Dispositivos' },
  { key: 'pagina', title: 'Páginas' },
  { key: 'campanha', title: 'Campanhas' },
  { key: 'midia', title: 'Mídias' },
  { key: 'palavra', title: 'Palavras-chave' },
]

const nf = new Intl.NumberFormat('pt-BR')
const rowLabel = (dim, option) => (dim === 'origem' ? originLabel(option.value) : option.label)

export default function CityCompare({
  cities, results, metric, metricLabel, onMetricChange, onApplyFilter, onRemove, onClose,
}) {
  const n = cities.length
  const loading = results.some((r) => r.loading)
  const errors = results.filter((r) => r.error)

  return (
    <aside className="mapa-drawer is-compare" aria-label={`Comparação de ${n} cidades`}>
      <header className="mapa-drawer-head">
        <div className="mapa-drawer-title">
          <strong>Comparando {n} cidades</strong>
          {loading && <span className="mapa-drawer-loading">atualizando...</span>}
        </div>
        <button type="button" className="mapa-drawer-close" onClick={onClose} aria-label="Fechar comparação">
          <Icon n="x" s={14} />
        </button>
      </header>

      <div className="cmp-chips">
        {cities.map((c, i) => (
          <span className="cmp-chip" key={`${c.uf}|${c.name}`} style={{ borderColor: COMPARE_COLORS[i] }}>
            <i className="cmp-dot" style={{ background: COMPARE_COLORS[i] }} />
            <span className="cmp-chip-name">{c.name}</span>
            <span className="bw-map-tt-uf">{c.uf}</span>
            <button
              type="button"
              className="cmp-chip-x"
              onClick={() => onRemove(c)}
              aria-label={`Remover ${c.name} da comparação`}
            >
              <Icon n="x" s={11} />
            </button>
          </span>
        ))}
      </div>

      {errors.length > 0 && <p className="mapa-drawer-error">Erro ao carregar: {errors[0].error}</p>}

      <section className="mapa-drawer-block">
        <h4 className="mf-title">Métricas</h4>
        <div className="cmp-metrics" role="radiogroup" aria-label="Métrica do mapa">
          {MAPA_METRICS.map((m) => {
            const values = results.map((r) => r.data?.totals?.[m.key])
            const max = Math.max(...values.map((v) => (typeof v === 'number' ? v : -1)))
            const lead = max > 0 && values.filter((v) => v === max).length < values.length ? max : null
            return (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={metric === m.key}
                className={`cmp-metric${metric === m.key ? ' is-active' : ''}`}
                style={{ gridTemplateColumns: `minmax(0,1fr) repeat(${n}, minmax(44px,auto))` }}
                onClick={() => onMetricChange(m.key)}
              >
                <span className="cmp-metric-label"><Icon n={m.icon} s={12} />{m.label}</span>
                {values.map((v, i) => (
                  <span
                    key={cities[i].name + cities[i].uf}
                    className={`cmp-metric-value${lead !== null && v === lead ? ' is-lead' : ''}`}
                  >
                    {typeof v === 'number' ? nf.format(v) : '—'}
                  </span>
                ))}
              </button>
            )
          })}
        </div>
        <p className="mapa-drawer-empty">Destaque = maior valor entre as cidades ({metricLabel} no mapa).</p>
      </section>

      {SECTIONS.map(({ key, title }) => (
        <section className="mapa-drawer-block" key={key}>
          <h4 className="mf-title">{title}</h4>
          <div className="cmp-cols" data-scroll style={{ gridTemplateColumns: `repeat(${n}, minmax(0,1fr))` }}>
            {cities.map((c, i) => {
              const rows = results[i].data?.breakdowns?.[key] || []
              const total = results[i].data?.total ?? 0
              return (
                <div className="cmp-col" key={`${c.uf}|${c.name}`} style={{ borderTopColor: COMPARE_COLORS[i] }}>
                  {rows.length === 0 && <p className="mapa-drawer-empty">{results[i].loading ? '...' : 'Sem dados'}</p>}
                  {rows.map((o) => {
                    const label = rowLabel(key, o)
                    const pct = total > 0 ? Math.round((o.count / total) * 100) : 0
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className="cmp-item"
                        title={`${c.name}: ${label} — clique para filtrar o mapa`}
                        onClick={() => onApplyFilter(key, o.value)}
                      >
                        <span className="cmp-item-name">{label}</span>
                        <span className="cmp-item-count">{nf.format(o.count)} <small>{pct}%</small></span>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </aside>
  )
}
